import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreditCheckAddress,
  CreditCheckCompanyData,
  CreditCheckObj,
  CreditCheckPersonalData,
  CreditCheckType,
} from './finapi';
import { JurPerson, NatPerson } from '@core/models/user';
import { RechtsformMap, land3alpha } from './mappingEvaDifa';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';

const FREIBERUFLER = 'FREIBERUFLER';
const MAX_COMPANY_NAME_LENGTH = 35;
const MAX_CREDIT_CHECK_ACCOUNTS = 2;

@Injectable({
  providedIn: 'root',
})
export class FinapiService {
  PROCESS_TOKEN: string;
  ACCESS_TOKEN: string;
  IMPORT_ID: string;
  selectedAccountId: string[] = [];
  creditCheckobj: CreditCheckObj;
  creditCheckType: CreditCheckType;

  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);

  getWebForm(lang: string): Observable<any> {
    return this.api.post('finapi/webFormUrl', { lang, userId: this.userId });
  }

  getAccounts(processToken: string, accessToken: string, importId: string): Observable<any> {
    return this.api.post('finapi/accounts', { processToken, accessToken, importId });
  }

  getWebFormStatus(accessToken: string, webFormId: string): Observable<any> {
    return this.api.post('finapi/getWebForm', { accessToken, webFormId, userId: this.userId });
  }

  checkIdentity(processToken: string, accessToken: string): Observable<any> {
    return this.api.post('finapi/checkIdentity', { processToken, accessToken, userId: this.userId });
  }

  creditCheck(processToken: string, accessToken: string, creditCheckType: CreditCheckType): Observable<any> {
    return this.api.post('finapi/creditCheck', {
      _id: this.userId,
      processToken,
      accessToken,
      creditCheckType,
      creditCheckobj: this.creditCheckobj,
      isGBR: this.auth.isRechtsformGBR(),
    });
  }

  creditCheckWithoutWebForm(creditCheckType: CreditCheckType): Observable<any> {
    return this.api.post('finapi/creditCheckWithoutWebForm', {
      _id: this.userId,
      creditCheckType,
      creditCheckobj: this.creditCheckobj,
      isGBR: this.auth.isRechtsformGBR(),
    });
  }

  getProccessToken(): Observable<any> {
    return this.api.post('finapi/getProccessToken', { _id: this.userId });
  }

  getAccessToken(processToken: string): Observable<any> {
    return this.api.post('finapi/getAccessToken', { _id: this.userId, processToken });
  }

  transactions(accessToken: string, page: number): Observable<any> {
    return this.api.post('finapi/transactions', {
      _id: this.userId,
      accessToken,
      selectedAccountId: this.selectedAccountId,
      page,
    });
  }

  finalizeProcess(processToken: string, accessToken: string): Observable<any> {
    return this.api.post('finapi/finalize', { processToken, accessToken });
  }

  postSelectedAccountList(selectedAccountList: unknown): Observable<any> {
    return this.api.post('finapi/selectedAccountList', { _id: this.userId, selectedAccountList });
  }

  buildCreditCheckobj(): void {
    this.creditCheckType = this.resolveCreditCheckType();
    this.creditCheckobj = {
      ...this.buildCreditCheckBase(),
      accounts: this.selectedAccountId.slice(0, MAX_CREDIT_CHECK_ACCOUNTS).map((accountId) => ({ accountId })),
    };
  }

  buildCreditCheckobjSimple(): void {
    this.creditCheckType = this.resolveCreditCheckType();
    this.creditCheckobj = this.buildCreditCheckBase();
  }

  private get userId(): string {
    return this.auth.user._id;
  }

  private resolveCreditCheckType(): CreditCheckType {
    const answer = this.auth.user.questions[0]?.answer;
    return answer?.kontokorrentkredit || answer?.kontoWechselService ? CreditCheckType.full : CreditCheckType.simple;
  }

  private buildCreditCheckBase(): CreditCheckObj {
    const natPerson = this.auth.user.natPersonen[this.auth.index];
    return {
      personalData: [this.buildPersonalData(natPerson)],
      companyData: this.buildCompanyData(natPerson, this.auth.user.JurPerson),
    };
  }

  private buildPersonalData(natPerson: NatPerson): CreditCheckPersonalData {
    const anrede = natPerson.Person.Anrede;
    return {
      gender: anrede === 'Herr' ? 'M' : anrede === 'Frau' ? 'F' : 'U',
      firstName: natPerson.Person.Vorname,
      lastName: natPerson.Person.Nachname,
      dateOfBirth: natPerson.Person.Geburtsdatum,
      currentAddress: {
        street: natPerson.Adresse.Strasse,
        zip: natPerson.Adresse.PLZ,
        city: natPerson.Adresse.Ort,
      },
    };
  }

  private buildCompanyData(natPerson: NatPerson, jurPerson: JurPerson): CreditCheckCompanyData {
    const isJurPerson = this.auth.isJurPerson();
    const rechtsform = isJurPerson ? this.resolveRechtsform(jurPerson) : FREIBERUFLER;

    return {
      companyType: rechtsform,
      companyName: isJurPerson
        ? jurPerson?.JurDaten.Firmenname
        : natPerson.Firmenname.substring(0, MAX_COMPANY_NAME_LENGTH),
      crefoIdentificationNumber: natPerson.CrefoID,
      currentAddress: this.buildCompanyAddress(natPerson, jurPerson, isJurPerson),
      companyLegalForm: rechtsform,
      companyRegisterType: isJurPerson ? (jurPerson.Registerdaten?.Registerart ?? '') : '',
      companyRegisterId: isJurPerson ? (jurPerson.Registerdaten?.Registernummer ?? '') : '',
      companyVATId: '',
    };
  }

  private buildCompanyAddress(natPerson: NatPerson, jurPerson: JurPerson, isJurPerson: boolean): CreditCheckAddress {
    if (isJurPerson) {
      return {
        street: jurPerson.Adresse.Strasse,
        zip: jurPerson.Adresse.PLZ,
        city: jurPerson.Adresse.Ort,
        country: land3alpha.get(jurPerson.Adresse.Land),
      };
    }

    const anschrift = natPerson.Firmenanschrift.Strasse ? natPerson.Firmenanschrift : natPerson.Adresse;
    return {
      street: anschrift.Strasse,
      zip: anschrift.PLZ,
      city: anschrift.Ort,
      country: land3alpha.get(anschrift.Land),
    };
  }

  private resolveRechtsform(jurPerson: JurPerson): string | undefined {
    const auspraegung = jurPerson?.JurDaten.RechtsformAuspraegung?.trim();
    return auspraegung ? RechtsformMap.get(auspraegung) : undefined;
  }
}
